/**
 * Locale-aware slug engine (step 1 §20): lowercase, unsafe characters removed, whitespace collapsed to hyphens,
 * locale-specific transliteration, optional Unicode letters. Pure and deterministic.
 */

// Per-language transliteration applied before generic accent stripping. German and Romanian read differently from French or Dutch.
const BY_LANGUAGE: Record<string, Record<string, string>> = {
  de: { ä: 'ae', ö: 'oe', ü: 'ue', ß: 'ss', Ä: 'ae', Ö: 'oe', Ü: 'ue' },
  fr: { œ: 'oe', Œ: 'oe', æ: 'ae', Æ: 'ae' },
  ro: { ă: 'a', â: 'a', î: 'i', ș: 's', ş: 's', ț: 't', ţ: 't', Ă: 'a', Â: 'a', Î: 'i', Ș: 's', Ț: 't' },
  nl: { ĳ: 'ij' },
  ru: {
    а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'e', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm', н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'kh', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'shch', ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
  },
};
// Applies to every language: ligatures and letters that do not decompose.
const COMMON: Record<string, string> = { ß: 'ss', æ: 'ae', œ: 'oe', ø: 'o', đ: 'd', ł: 'l', ð: 'd', þ: 'th', ı: 'i' };

export const languageOf = (locale: string) => locale.toLowerCase().split('-')[0]!;

export interface SlugOptions {
  /** Keep letters and digits of any script instead of transliterating to ASCII. */
  unicode?: boolean;
  maxLength?: number;
}

export function slugify(input: string, locale = 'en', opts: SlugOptions = {}): string {
  const lang = languageOf(locale);
  const table = BY_LANGUAGE[lang] ?? {};
  const max = opts.maxLength ?? 80;
  let s = input.trim().toLowerCase();
  if (!opts.unicode) {
    s = [...s].map((ch) => table[ch] ?? COMMON[ch] ?? ch).join('');
    // Cyrillic (and other scripts) have no table outside their language: fall back to the Russian map so slugs stay readable.
    s = [...s].map((ch) => BY_LANGUAGE.ru![ch] ?? ch).join('');
    s = s.normalize('NFKD').replace(/\p{M}+/gu, '');
    s = s.replace(/[^a-z0-9]+/g, '-');
  } else {
    s = s.normalize('NFKC').replace(/[^\p{L}\p{N}]+/gu, '-');
  }
  s = s.replace(/-{2,}/g, '-').replace(/^-+|-+$/g, '');
  if (s.length > max) s = s.slice(0, max).replace(/-+$/g, '');
  return s;
}

/** Slugify each segment of a path; empty segments are dropped. `Services / CRM Entwicklung` → `services/crm-entwicklung`. */
export function slugifyPath(input: string, locale = 'en', opts: SlugOptions = {}): string {
  return input.split('/').map((seg) => slugify(seg, locale, opts)).filter(Boolean).join('/');
}

/** First path segments the platform owns. A page slug can never start with one of these. */
export const RESERVED_ROOTS = ['api', 'preview', '_next', 'sitemap', 'sitemaps', 'robots', 'admin', 'static', 'favicon'] as const;

const SEGMENT_ASCII = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const SEGMENT_UNICODE = /^[\p{L}\p{N}]+(?:-[\p{L}\p{N}]+)*$/u;

/** A stored page path: lowercase segments joined by "/", no leading/trailing slash. The home page is the empty path. */
export function validatePagePath(path: string, opts: { unicode?: boolean; allowEmpty?: boolean } = {}): string | null {
  if (path === '') return opts.allowEmpty ? null : 'A page needs a URL path.';
  if (path.length > 200) return 'The URL path is too long (200 characters max).';
  const segs = path.split('/');
  const re = opts.unicode ? SEGMENT_UNICODE : SEGMENT_ASCII;
  for (const seg of segs) {
    if (!seg) return 'The URL path has an empty segment.';
    if (seg.length > 80) return 'A URL segment is too long (80 characters max).';
    if (!re.test(seg)) return 'Use lowercase letters, digits and single hyphens between words.';
  }
  if ((RESERVED_ROOTS as readonly string[]).includes(segs[0]!)) return `"${segs[0]}" is reserved for the platform.`;
  return null;
}
